import React, { useEffect, useState } from 'react';
import {
  Card, CardBody, Col, Row, Button, Table,
  Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, Spinner
} from 'reactstrap';
import { get, postJson, putJson, del } from '../../../hook/request';
import { toast } from 'react-toastify';

type NoteItem = {
  ID: number;
  utenteID: number;
  clienteID: string;
  note: string;
  data_ins: string;
  rdvID: number;
  nome?: string;
  cognome?: string;
  nome_rdv?: string;
};

type NotesSectionProps = {
  consumerId: string; // <- consumer_id (es. "14283070")
};

export default function NotesSection({ consumerId }: NotesSectionProps) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<NoteItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Modale: Modifica
  const [editing, setEditing] = useState<{ id: number; text: string } | null>(null);

  // Modale: Crea
  const [createOpen, setCreateOpen] = useState(false);
  const [newText, setNewText] = useState('');
  const [creating, setCreating] = useState(false);

  async function fetchNotes() {
    if (!consumerId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await get<any>('/note/list', { clienteID: consumerId });
      const payload = (res as any)?.data ?? res;
      const list: NoteItem[] = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : [];
      setRows(list);
    } catch (e: any) {
      setError(e?.message || 'Errore nel caricamento note');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchNotes();
  }, [consumerId]);

  // --- EDIT ---
  function openEdit(n: NoteItem) {
    setEditing({ id: n.ID, text: n.note ?? '' });
  }
  function closeEdit() {
    setEditing(null);
  }
  async function saveEdit() {
    if (!editing) return;
    try {
      await putJson('/note/edit', { id: editing.id, text: editing.text });
      toast.success('Nota aggiornata');
      closeEdit();
      fetchNotes();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Errore aggiornamento');
    }
  }

  // --- DELETE ---
  async function deleteNote(id: number) {
    if (!window.confirm('Eliminare definitivamente la nota?')) return;
    try {
      await del(`/note?ID=${id}`); 
      toast.success('Nota eliminata');
      fetchNotes();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Errore eliminazione');
    }
  }

  // --- CREATE ---
  function openCreate() {
    setNewText('');
    setCreateOpen(true);
  }
  function closeCreate() {
    if (creating) return;
    setCreateOpen(false);
  }
  async function saveCreate() {
    if (!newText.trim()) {
      toast.warn('Scrivi il testo della nota');
      return;
    }
    try {
      setCreating(true);
      await postJson('/note/new', {
        clienteID: String(consumerId),
        text: newText,
        rdvID: 0
      });
      toast.success('Nota creata');
      setCreateOpen(false);
      fetchNotes();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Errore creazione');
    } finally {
      setCreating(false);
    }
  }

  return (
    <>
      <Row className="g-3">
        <Col xl={12}>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 className="mb-0">Lista note</h5>
                  <small className="text-muted">Cliente ID: {consumerId}</small>
                </div>
                <Button color="primary" size="sm" onClick={openCreate}>
                  + Nuova nota
                </Button>
              </div>

              {loading ? (
                <div className="py-4 text-center">
                  <Spinner size="sm" className="me-2" /> Caricamento...
                </div>
              ) : error ? (
                <div className="alert alert-danger mb-0">{error}</div>
              ) : rows.length === 0 ? (
                <div className="text-center text-muted py-4">Nessuna nota presente.</div>
              ) : (
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: 220 }}>Creato da</th>
                        <th style={{ width: 260 }}>File</th>
                        <th>Testo</th>
                        <th style={{ width: 160 }}>Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((n) => {
                        const creatoDa =
                          [n.cognome, n.nome].filter(Boolean).join(' ') ||
                          `Utente #${n.utenteID}`;
                        return (
                          <tr key={n.ID}>
                            <td>
                              <div className="fw-semibold">{creatoDa}</div>
                            </td>
                            <td>{n.nome_rdv || '-'}</td>
                            <td>{n.note || '-'}</td>
                            <td className="d-flex gap-2">
                              <Button size="sm" color="warning" onClick={() => openEdit(n)}>
                                Modifica
                              </Button>
                              <Button
                                size="sm"
                                color="danger"
                                onClick={() => deleteNote(n.ID)}
                              >
                                Elimina
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* MODALE MODIFICA */}
      <Modal isOpen={!!editing} toggle={closeEdit} centered>
        <ModalHeader toggle={closeEdit}>Modifica nota</ModalHeader>
        <ModalBody>
          <Label htmlFor="editText" className="mb-1">
            Testo
          </Label>
          <Input
            id="editText"
            type="textarea"
            rows={6}
            value={editing?.text || ''}
            onChange={(e) =>
              setEditing((p) => (p ? { ...p, text: e.target.value } : p))
            }
            placeholder="Aggiorna il testo della nota…"
          />
        </ModalBody>
        <ModalFooter>
          <Button style={{color:'black'}} color="light" onClick={closeEdit}>
            Annulla
          </Button>
          <Button
            color="success"
            onClick={saveEdit}
            disabled={!editing?.text?.trim()}
          >
            Salva
          </Button>
        </ModalFooter>
      </Modal>

      {/* MODALE NUOVA */}
      <Modal isOpen={createOpen} toggle={closeCreate} centered>
        <ModalHeader toggle={closeCreate}>Nuova nota</ModalHeader>
        <ModalBody>
          <Label htmlFor="newText">Testo</Label>
          <Input
            id="newText"
            type="textarea"
            rows={6}
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            placeholder="Scrivi qui la nota…"
          />
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={closeCreate} disabled={creating}>
            Annulla
          </Button>
          <Button
            color="success"
            onClick={saveCreate}
            disabled={creating || !newText.trim()}
          >
            {creating ? (
              <>
                <Spinner size="sm" className="me-2" /> Salvataggio…
              </>
            ) : (
              'Salva'
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
